import { shortLap } from '../net/lapTime';
import { type Board, type BoardEntry, type Caught, NAME_MAX } from '../net/protocol';
import { el, formatDelta, formatLap } from './format';

/** Discord's logo (its own shape, unchanged), drawn in the colour of what holds it. */
export const DISCORD_GLYPH = '<svg viewBox="0 0 127.14 96.36" aria-hidden="true"><path fill="currentColor" d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/></svg>';

/**
 * Discord's logo: by a name on a board (that lap was set inside Discord, or signed in with Discord on the website), or
 * beside words that say it (`said`).
 */
export function discordMark(parent: HTMLElement, said = false): void {
  const m = el('span', 'discord-mark', parent);
  m.innerHTML = DISCORD_GLYPH;
  if (said) {
    m.setAttribute('aria-hidden', 'true');
    return;
  }
  m.setAttribute('role', 'img');
  m.setAttribute('aria-label', `${MARK_TIP[0]}: ${MARK_TIP[1]}`);
  m.tabIndex = 0;
  // What it means, in a little card over it: under the mouse, a tap (phones, and inside Discord, where a title never
  // shows), or the keyboard.
  m.onpointerenter = (ev) => ev.pointerType === 'mouse' && showTip(m);
  m.onpointerleave = (ev) => ev.pointerType === 'mouse' && hideTip();
  m.onclick = (ev) => (ev as PointerEvent).pointerType !== 'mouse' && (tipFor === m ? hideTip() : showTip(m, 4000));
  m.onfocus = () => m.matches(':focus-visible') && showTip(m);
  m.onblur = () => hideTip();
}

const MARK_TIP = ['Discord player', 'Races inside Discord, or signed in with Discord on the website.'] as const;
let tipEl: HTMLElement | null = null;
let tipFor: HTMLElement | null = null;
let tipTimer = 0;

/** The card over a logo, kept in view (above it, or under it at the top of the screen). `ms`: put away after. */
function showTip(m: HTMLElement, ms = 0): void {
  if (!tipEl) {
    tipEl = el('div', 'mark-tip', document.body);
    tipEl.setAttribute('role', 'tooltip');
    const b = el('b', '', tipEl);
    discordMark(b, true);
    b.append(MARK_TIP[0]);
    el('span', '', tipEl, MARK_TIP[1]);
    // A tap anywhere else, a scroll, Escape, or the board drawn again without that logo: away it goes.
    addEventListener('pointerdown', (ev) => !(ev.target instanceof Node && tipFor?.contains(ev.target)) && hideTip(), true);
    addEventListener('pointermove', () => tipFor && !tipFor.isConnected && hideTip(), true);
    addEventListener('scroll', () => hideTip(), true);
    addEventListener('keydown', (ev) => ev.key === 'Escape' && hideTip(), true);
  }
  const t = tipEl;
  tipFor = m;
  clearTimeout(tipTimer);
  if (ms) tipTimer = window.setTimeout(hideTip, ms);
  t.hidden = false;
  const r = m.getBoundingClientRect();
  const w = t.offsetWidth;
  const h = t.offsetHeight;
  const x = Math.max(8, Math.min(innerWidth - w - 8, r.left + r.width / 2 - w / 2));
  const below = r.top - h - 8 < 8;
  t.classList.toggle('below', below);
  t.style.left = `${x}px`;
  t.style.top = `${below ? r.bottom + 8 : r.top - h - 8}px`;
  t.style.setProperty('--at', `${r.left + r.width / 2 - x}px`);
}

function hideTip(): void {
  clearTimeout(tipTimer);
  tipFor = null;
  if (tipEl) tipEl.hidden = true;
}

/**
 * Today's leaderboard, one side of the title poster. Names are only ever set
 * as text, never as HTML.
 */
export type BoardStatus = 'loading' | 'ready' | 'offline' | 'preview' | 'closed' | 'future';

/** Inside a Discord server: whose board is shown, the server's own or everyone's. */
export interface BoardScope {
  /** The server's name, as Discord gives it. */
  server: string;
  on: 'server' | 'everyone';
  /** Your best lap posts in the server's channel when you leave (the switch under it, on the leaderboard). */
  posts?: boolean;
  /** The channel shows you while you drive ("on track now", with buttons to watch you or race with you). */
  live?: boolean;
}

/** The switch between a server's board and everyone's: two halves of one button, the board shown filled. */
export function scopeSwitch(parent: HTMLElement, s: BoardScope, pick: (on: BoardScope['on']) => void): void {
  const box = el('div', 'scope', parent);
  box.setAttribute('role', 'group');
  box.setAttribute('aria-label', 'Whose board');
  for (const [on, label] of [['server', s.server], ['everyone', 'everyone']] as const) {
    const b = el('button', `scope-half${s.on === on ? ' on' : ''}`, box, label);
    b.type = 'button';
    b.setAttribute('aria-pressed', String(s.on === on));
    if (on === 'server') b.title = `${s.server}: the players who race in this server`;
    b.onclick = () => s.on !== on && pick(on);
  }
}

export interface BoardView {
  status: BoardStatus;
  board: Board | null;
  circuit: string;
  sub: string;
  /** Your best clean lap today on this circuit and car, or null. */
  bestMs: number | null;
  name: string;
  busy: boolean;
  /** Show the name form even if your best is already on the board (renaming). */
  editing: boolean;
  message: string | null;
  /** Rows can be watched (the lap replayed, then raced as a ghost). */
  watchable: boolean;
  /** Where you stand on the ladder ("▸ Marco's lap is unlocked: your next target"), or null. */
  ladder: string | null;
  /** Your lap can go out as a challenge (on today's open board): a chip on your row, a button at the foot. */
  challenge: boolean;
  /** Inside a Discord server: the switch between its board and everyone's. */
  scope?: BoardScope | null;
  /** On the website, with a Discord app: signing in with it (`as`: the name signed in as, or null). */
  signIn?: { as: string | null } | null;
}

export class BoardPanel {
  readonly root: HTMLElement;
  onSave: (name: string) => void = () => {};
  onEdit: () => void = () => {};
  onBack: () => void = () => {};
  /** Watch that row's lap. */
  onWatch: (e: BoardEntry) => void = () => {};
  /** Send your lap to a friend as a challenge. */
  onChallenge: () => void = () => {};
  /** Inside a Discord server: show its board, or everyone's. */
  onScope: (on: BoardScope['on']) => void = () => {};
  /** Inside a Discord server: your best laps posted in it, or not. */
  onPosts: (on: boolean) => void = () => {};
  onLiveShown: (on: boolean) => void = () => {};
  /** On the website: sign in with Discord. */
  onSignIn: () => void = () => {};
  private scopeEl: HTMLElement;
  private view: BoardView | null = null;
  /** For a moment after sharing, what happened ("link copied ✓"), on the challenge button. */
  private said: string | null = null;
  private watchable = false;
  private circuitEl: HTMLElement;
  private subEl: HTMLElement;
  private rowsEl: HTMLElement;
  private footEl: HTMLElement;
  private input: HTMLInputElement;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'board', parent);
    const back = el('button', 'back', this.root, '◂ back');
    back.type = 'button';
    back.onclick = () => this.onBack();
    el('div', 'kicker', this.root, 'today’s leaderboard');
    this.circuitEl = el('h3', '', this.root);
    this.subEl = el('div', 'sub', this.root);
    this.scopeEl = el('div', 'scope-row', this.root);
    this.rowsEl = el('div', 'rows', this.root);
    // Scrolling the list yourself: it stays where you put it.
    for (const ev of ['wheel', 'touchstart', 'keydown']) this.rowsEl.addEventListener(ev, () => (this.revealing = false), { passive: true });
    this.footEl = el('div', 'foot', this.root);
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.maxLength = NAME_MAX * 2;
    this.input.placeholder = 'Your name';
    this.input.setAttribute('autocomplete', 'nickname');
    this.input.spellcheck = false;
    this.input.enterKeyHint = 'done';
  }

  render(v: BoardView): void {
    this.view = v;
    this.watchable = v.watchable;
    this.target = v.board?.target ?? null;
    this.rowsEl.classList.toggle('watchable', v.watchable);
    this.circuitEl.textContent = v.circuit.toLowerCase();
    this.subEl.textContent = v.sub;
    this.scopeEl.replaceChildren();
    this.scopeEl.hidden = !v.scope;
    if (v.scope) scopeSwitch(this.scopeEl, v.scope, (on) => this.onScope(on));
    const posts = v.scope?.posts;
    if (posts !== undefined) {
      const b = el('button', `scope-posts${posts ? ' on' : ''}`, this.scopeEl, `${posts ? '☑' : '☐'} post my best lap here when I leave`);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(posts));
      b.title = posts ? 'Your best lap of the day goes in this channel, with its picture, when you close the game' : 'Your laps aren’t posted in this server';
      b.onclick = () => this.onPosts(!posts);
    }
    const live = v.scope?.live;
    if (live !== undefined) {
      const b = el('button', `scope-posts${live ? ' on' : ''}`, this.scopeEl, `${live ? '☑' : '☐'} show me here while I drive`);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(live));
      b.title = live
        ? 'While you’re on track, this channel says so, with buttons for friends to watch you or race with you'
        : 'This channel doesn’t say when you’re on track (friends inside the game still see you)';
      b.onclick = () => this.onLiveShown(!live);
    }
    this.renderRows(v);
    this.renderFoot(v);
  }

  private renderRows(v: BoardView): void {
    const box = this.rowsEl;
    // A refresh keeps the list where it was scrolled to.
    const scrolled = box.scrollTop;
    this.fillRows(v);
    box.scrollTop = scrolled;
    this.revealYou();
  }

  private fillRows(v: BoardView): void {
    const box = this.rowsEl;
    box.replaceChildren();
    const b = v.board;
    if (v.status === 'loading' && !b) {
      el('div', 'note', box, 'Loading…');
      return;
    }
    if (!b) {
      el('div', 'note', box, v.status === 'preview' ? 'Preview · these laps stay off the board' : 'Leaderboard offline · your laps are still saved on this device');
      return;
    }
    if (b.entries.length === 0) {
      el('div', 'note', box, v.status === 'future' ? 'Not open yet' : 'No times yet · be the first on the board');
      return;
    }
    // Discord's logo by the laps set there, except on a server's own board (every lap on it is).
    const marks = v.scope?.on !== 'server';
    for (const e of b.entries) this.row(box, e, v.challenge, marks);
    // Below the top: your next target and you, wherever you are.
    const extra = [b.target, b.you].filter((e): e is BoardEntry => !!e && !b.entries.some((x) => x.rank === e.rank));
    if (extra.length) {
      if (extra[0].rank > b.entries.length + 1) el('div', 'more', box, '⋯');
      for (const e of extra) this.row(box, e, v.challenge, marks);
    }
    el('div', 'count', box, `${b.total} ${b.total === 1 ? 'driver' : 'drivers'}`);
    // The corner of shame: the day's made-up laps the stewards caught, under the board (never among it).
    if (b.caught?.length) {
      const h = el('div', 'dsq-head', box);
      el('b', '', h, 'i furbetti');
      h.append(b.closed ? ' · caught by the stewards' : ' · caught by the stewards today');
      for (const c of b.caught) this.dsqRow(box, c);
    }
  }

  /** A made-up lap the stewards caught: its claimed time struck out, what it really took beside it. */
  private dsqRow(box: HTMLElement, c: Caught): void {
    const r = el('div', 'row dsq', box);
    r.title = `Handed in as ${shortLap(c.claimedMs)}; driven again from its own inputs, it took ${shortLap(c.droveMs)}${c.tries > 1 ? ` · sent ${c.tries} times` : ''}`;
    el('span', 'rank', r, 'DSQ');
    const name = el('span', 'name', r);
    el('span', 'who', name, c.name ?? 'a borrowed name');
    if (c.aka.length) el('span', 'aka', name, `+${c.aka.length}`).title = `Also sent as ${c.aka.join(', ')}`;
    el('s', 'time', r, shortLap(c.claimedMs));
    el('span', 'gap', r, shortLap(c.droveMs));
  }

  private target: BoardEntry | null = null;
  /** The board was just opened: scroll the list to your row once it's there. */
  private revealing = false;

  /** Opening the board: your row in view (the list runs to the top hundred), now or once it loads. */
  reveal(): void {
    this.revealing = true;
    this.rowsEl.scrollTop = 0;
    this.revealYou();
  }

  private revealYou(): void {
    const box = this.rowsEl;
    const you = this.revealing ? box.querySelector<HTMLElement>('.row.you') : null;
    // Not shown (another side of the poster) or not on the board yet: wait.
    if (!you || box.clientHeight === 0) return;
    this.revealing = false;
    const b = box.getBoundingClientRect();
    const r = you.getBoundingClientRect();
    if (r.top >= b.top && r.bottom <= b.bottom) return;
    box.scrollTop += r.top - b.top - (b.height - r.height) / 2;
  }

  private row(box: HTMLElement, e: BoardEntry, challenge = false, marks = true): void {
    const isTarget = !!this.target && !e.you && e.rank === this.target.rank;
    const r = el('div', 'row' + (e.you ? ' you' : '') + (isTarget ? ' target' : ''), box);
    el('span', 'rank', r, String(e.rank));
    const name = el('span', 'name', r);
    el('span', 'who', name, e.name);
    if (marks && e.discord) discordMark(name);
    // Your own row: send it to a friend from right there.
    if (e.you && challenge) {
      const c = el('button', 'chip', name);
      el('span', 'long', c, 'challenge ');
      c.append('↗\uFE0E');
      c.type = 'button';
      c.setAttribute('aria-label', 'Challenge your friends to beat your lap');
      c.onclick = () => this.onChallenge();
    }
    // A lap under a minute without its "0:" (as everywhere a lap is quoted), so the name keeps the room.
    el('span', 'time', r, formatLap(e.lapMs).replace(/^0:/, ''));
    el('span', 'gap', r, e.rank === 1 ? '' : formatDelta(e.gapMs));
    if (!this.watchable) return;
    // Every time is there to see; only your own lap and your next target can be watched.
    if (!e.ref) {
      const lock = el('span', 'watch locked', r, '🔒');
      lock.title = e.rank === 1 ? 'The leader’s lap stays secret till tomorrow' : 'Ahead of you: only your next target can be watched';
      return;
    }
    const w = el('button', 'watch', r, '▸');
    w.type = 'button';
    w.title = e.you ? 'Watch your lap' : `Watch ${e.name}'s lap · your next target`;
    w.setAttribute('aria-label', w.title);
    w.onclick = () => this.onWatch(e);
  }

  private renderFoot(v: BoardView): void {
    const f = this.footEl;
    f.replaceChildren();
    const you = v.board?.you ?? null;
    const saved = you !== null && v.bestMs !== null && you.lapMs <= v.bestMs;
    if (v.status === 'preview' || v.status === 'closed' || v.status === 'future' || v.status === 'offline') {
      if (v.status === 'closed') el('div', 'note', f, 'Leaderboard closed · this day is over');
      if (you) el('div', 'standing', f, `You were P${you.rank} of ${v.board?.total ?? 0} · as ${you.name}`);
      if (v.message) el('div', 'msg', f, v.message);
      return;
    }
    if (v.bestMs === null && !you) {
      el('div', 'note', f, 'Set a clean lap to get on the board, and to unlock the lap of the driver ahead of you');
      return;
    }
    if (saved && !v.editing) {
      const s = el('div', 'standing', f);
      s.append(`P${you.rank} of ${v.board?.total ?? 0} · as `);
      el('b', '', s, you.name);
      if (v.ladder) el('div', 'ladder', f, v.ladder);
      // The big ask, where everyone looks: send your lap out as a challenge.
      if (v.challenge) {
        const c = el('button', 'challenge', f, this.said ?? 'challenge your friends ↗\uFE0E');
        c.type = 'button';
        c.classList.toggle('said', !!this.said);
        c.onclick = () => this.onChallenge();
        el('div', 'why', f, `they watch your ${formatLap(you.lapMs)}, then race your ghost · before midnight`);
      }
      // Signed in with Discord: the name is Discord's (the same in both places). Else the name is yours to change.
      if (v.signIn?.as) this.signedIn(f);
      else {
        const edit = el('button', 'link', f, 'change name');
        edit.type = 'button';
        edit.onclick = () => this.onEdit();
        this.signInLine(f, v);
      }
      if (v.message) el('div', 'msg', f, v.message);
      return;
    }
    // Your best isn't on the board yet (or you're renaming): the name form.
    const form = el('form', 'save', f);
    const label = el('div', 'mine', form);
    label.append('Your lap ');
    el('b', '', label, v.bestMs !== null ? formatLap(v.bestMs) : '—');
    if (!saved && v.bestMs !== null) el('div', 'why', form, 'save it, then challenge your friends to beat it');
    const line = el('div', 'line', form);
    line.appendChild(this.input);
    if (document.activeElement !== this.input) this.input.value = v.name;
    const go = el('button', 'go', line, v.busy ? '…' : 'save');
    go.type = 'submit';
    go.disabled = v.busy || v.bestMs === null;
    form.onsubmit = (e) => {
      e.preventDefault();
      if (!v.busy) this.onSave(this.input.value);
    };
    this.signInLine(f, v);
    if (v.message) el('div', 'msg', f, v.message);
  }

  /** On the website, not signed in: the way to sign in with Discord, and what it's for. */
  private signInLine(f: HTMLElement, v: BoardView): void {
    if (!v.signIn || v.signIn.as !== null) return;
    const b = el('button', 'link signin', f);
    b.type = 'button';
    discordMark(b, true);
    b.append('sign in with Discord');
    el('span', 'for', b, 'your laps on every device, and in your servers');
    b.onclick = () => this.onSignIn();
  }

  private signedIn(f: HTMLElement): void {
    const s = el('div', 'signed', f);
    discordMark(s, true);
    s.append('signed in with Discord');
  }

  /** What happened when you shared ("link copied ✓"), on the challenge button for a moment; null to clear. */
  say(text: string | null): void {
    this.said = text;
    if (this.view) this.renderFoot(this.view);
  }

  focusName(): void {
    this.input.focus();
    this.input.select();
  }
}
